export const crmSections = [
  {
    key: "dashboard",
    label: "Dashboard",
    path: "/crm/dashboard",
    description: "Kennzahlen und Handlungsbedarf",
  },
  {
    key: "leads",
    label: "Interessenten",
    path: "/crm/leads",
    description: "Interessenten bearbeiten",
  },
  {
    key: "organizations",
    label: "Organisationen",
    path: "/crm/organisationen",
    description: "Unternehmen und Kundenakten",
  },
  {
    key: "contacts",
    label: "Kontakte",
    path: "/crm/kontakte",
    description: "Ansprechpersonen verwalten",
  },
  {
    key: "deals",
    label: "Vertriebsvorgänge",
    path: "/crm/deals",
    description: "Vertriebspipeline steuern",
  },
  {
    key: "offers",
    label: "Angebote",
    path: "/crm/angebote",
    description: "Angebote und Bestätigungen",
  },
  {
    key: "communications",
    label: "Kommunikation",
    path: "/crm/kommunikation",
    description: "Kundenvorgänge dokumentieren",
  },
  {
    key: "documents",
    label: "Dokumente",
    path: "/crm/dokumente",
    description: "Geschützte CRM-Dateien",
  },
  {
    key: "activities",
    label: "Aktivitäten",
    path: "/crm/aktivitaeten",
    description: "Aufgaben und Termine",
  },
] as const;

export const crmPrimarySections = [
  {
    key: "sales",
    label: "Vertrieb",
    path: "/crm/vertrieb",
    description: "Interessenten und laufende Vorgänge",
  },
  {
    key: "customers",
    label: "Kunden",
    path: "/crm/kunden",
    description: "Unternehmen und Ansprechpartner",
  },
  {
    key: "activities",
    label: "Aktivitäten",
    path: "/crm/aktivitaeten",
    description: "Aufgaben und Termine",
  },
  {
    key: "email",
    label: "E-Mail-Eingang",
    path: "/crm/e-mail",
    description: "Persönliche Kundenmails zuordnen und bearbeiten",
  },
  {
    key: "more",
    label: "Mehr",
    path: "/crm/mehr",
    description: "Fachlisten und Verwaltung",
  },
] as const;

export type CrmPrimarySection = (typeof crmPrimarySections)[number]["key"];

export function crmPrimarySectionFromPath(
  pathname: string,
): (typeof crmPrimarySections)[number] | undefined {
  return crmPrimarySections.find(
    (section) => pathname === section.path || pathname.startsWith(`${section.path}/`),
  );
}

export type CrmSection = (typeof crmSections)[number]["key"];

export function isCrmSection(value: string | undefined): value is CrmSection {
  return crmSections.some((section) => section.key === value);
}

export function crmSectionPath(section: CrmSection): string {
  return crmSections.find((entry) => entry.key === section)?.path ?? "/crm/dashboard";
}

export function crmSectionFromPath(pathname: string): (typeof crmSections)[number] | undefined {
  return crmSections.find(
    (section) => pathname === section.path || pathname.startsWith(`${section.path}/`),
  );
}

export function crmBreadcrumbItems(pathname: string): { label: string; path?: string }[] | null {
  if (pathname !== "/crm" && !pathname.startsWith("/crm/")) return null;
  const root = { label: "CRM", path: "/crm/vertrieb" };
  if (pathname === "/crm/kontakte/zielgruppen") {
    return [root, { label: "Kontakte", path: "/crm/kontakte" }, { label: "Zielgruppen" }];
  }
  const additional: Record<string, string> = {
    "/crm/ereignisse": "Ereignisse",
    "/crm/datenqualitaet": "Datenqualität",
    "/crm/anrufe": "Nicht zugeordnete Anrufe",
  };
  const label =
    additional[pathname] ??
    (crmPrimarySectionFromPath(pathname) ?? crmSectionFromPath(pathname))?.label;
  return label ? [root, { label }] : [root];
}

export function crmOrganizationPath(organizationId: string): string {
  return crmCustomerPath(organizationId);
}

/**
 * The customer workspace is the user-facing home of an organization record.
 * "Organization" remains the technical/API term; keeping this helper separate
 * makes that boundary explicit at call sites.
 */
export function crmCustomerPath(organizationId: string): string {
  return `/crm/kunden/${encodeURIComponent(organizationId)}`;
}

export function crmRecordPath(section: Exclude<CrmSection, "dashboard">, recordId: string): string {
  if (section === "leads") return crmSalesPath("lead", recordId);
  if (section === "deals") return crmSalesPath("opportunity", recordId);
  return `${crmSectionPath(section)}/${encodeURIComponent(recordId)}`;
}

export function crmSalesPath(type: "lead" | "opportunity", recordId: string): string {
  return `/crm/vertrieb/${type}/${encodeURIComponent(recordId)}`;
}
